import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, X as XIcon, Download } from 'lucide-react';

function detectPlatform() {
  if (typeof window === 'undefined') return { platform: 'unknown', isStandalone: false };
  const ua = window.navigator.userAgent || '';
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  // iPadOS 13+ reports as "Macintosh" — use touch-points to distinguish.
  const isIpadOS = /Mac/.test(ua) && (window.navigator.maxTouchPoints || 0) > 1;
  if (/iPad|iPhone|iPod/.test(ua) || isIpadOS) return { platform: 'ios', isStandalone };
  if (/android/i.test(ua)) return { platform: 'android', isStandalone };
  return { platform: 'desktop', isStandalone };
}

/**
 * Floating install prompt. Reads the globally-captured beforeinstallprompt event
 * (caught in index.html before React mounts) so we never miss it. On iOS/iPadOS
 * we show manual instructions after a short delay since iOS doesn't expose a
 * programmatic install API.
 */
export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [plat, setPlat] = useState({ platform: 'unknown', isStandalone: false });

  useEffect(() => {
    const info = detectPlatform();
    setPlat(info);
    if (info.isStandalone) return;
    if (typeof window === 'undefined') return;

    try {
      const ts = Number(window.localStorage?.getItem('pm_install_dismissed') || 0);
      if (ts && Date.now() - ts < 14 * 24 * 60 * 60 * 1000) return;
    } catch { /* ignore */ }

    if (window.__pmDeferredInstall) {
      setDeferred(window.__pmDeferredInstall);
      setShow(true);
    }

    const onReady = () => {
      if (window.__pmDeferredInstall) {
        setDeferred(window.__pmDeferredInstall);
        setShow(true);
      }
    };
    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
    };

    window.addEventListener('pm-install-ready', onReady);
    window.addEventListener('pm-app-installed', onInstalled);

    if (info.platform === 'ios') {
      const t = setTimeout(() => setShow(true), 2200);
      return () => {
        clearTimeout(t);
        window.removeEventListener('pm-install-ready', onReady);
        window.removeEventListener('pm-app-installed', onInstalled);
      };
    }
    return () => {
      window.removeEventListener('pm-install-ready', onReady);
      window.removeEventListener('pm-app-installed', onInstalled);
    };
  }, []);

  const install = async () => {
    const ev = deferred || window.__pmDeferredInstall;
    if (!ev) return;
    try {
      await ev.prompt();
      const choice = await ev.userChoice;
      if (choice.outcome === 'accepted') setShow(false);
    } catch { /* ignore */ }
    window.__pmDeferredInstall = null;
    setDeferred(null);
  };

  const dismiss = () => {
    setShow(false);
    try { window.localStorage?.setItem('pm_install_dismissed', String(Date.now())); } catch { /* ignore */ }
  };

  if (!show || plat.isStandalone) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md bg-navy border-2 border-gold/60 rounded-lg shadow-xl p-3 sm:p-4 flex items-start gap-3 animate-glow-pulse"
    >
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center">
        <Smartphone className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-h uppercase text-sm text-gold tracking-wider mb-0.5">Instalar PlayMaster IA</div>
        {plat.platform === 'ios' ? (
          <p className="text-xs text-gray-300 leading-relaxed">
            En Safari toca <strong>Compartir ⤴</strong> y luego <strong>&ldquo;Añadir a pantalla de inicio&rdquo;</strong>.
          </p>
        ) : (
          <p className="text-xs text-gray-300 leading-relaxed">
            Instala la app y ábrela con un toque, sin barra del navegador.
          </p>
        )}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {plat.platform !== 'ios' && (deferred || window.__pmDeferredInstall) && (
            <button
              data-testid="pwa-install-btn"
              onClick={install}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-gold text-navy text-xs font-h uppercase tracking-wider font-semibold hover:bg-gold-light"
            >
              <Download className="w-3.5 h-3.5" /> Instalar
            </button>
          )}
          <Link to="/install" data-testid="pwa-install-more" className="text-[11px] text-gold hover:underline">
            Ver instrucciones
          </Link>
        </div>
      </div>
      <button onClick={dismiss} data-testid="pwa-install-dismiss" className="flex-shrink-0 p-1 text-gray-400 hover:text-white" aria-label="Cerrar">
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
